import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import apiInstance from "../apiInstance";
import PopupModal from "./popups/PopupModal";
import { changeCurrentUser } from "../../state/actionCreators/index";
import { formatDate } from "../Functions/formatDate";
import SubscriptionDetails from "./SubscriptionDetails";

const DevadminOrders = ({ user, changeCurrentUser }) => {
  const [error, setError] = useState("");
  const [newOrder, setNewOrder] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const popupModalRef = useRef(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [widgetTypes, setWidgetTypes] = useState([]);

  // Filter states
  const [searchName, setSearchName] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [dateFilterOption, setDateFilterOption] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);

  // Status options
  const statusOptions = [
    "active",
    "incomplete",
    "past_due",
    "expired",
    "canceled",
  ];

  // Date filter options
  const dateFilterOptions = ["Today", "Last 7 days", "This month", "Custom"];

  // Load subscriptions on component mount
  useEffect(() => {
    loadSubscriptions();
  }, [newOrder]);
  useEffect(() => {
    loadFilterData();
  }, []);

  // Apply filters when filter states change
  useEffect(() => {
    applyFilters();
  }, [
    subscriptions,
    searchName,
    selectedCustomer,
    selectedStatus,
    selectedType,
    minAmount,
    maxAmount,
    dateFilterOption,
    startDate,
    endDate,
  ]);

  // Load filter dropdown data
  const loadFilterData = async () => {
    try {
      const usersResponse = await apiInstance.get('devadmin/user');
      const widgetsResponse = await apiInstance.get('devadmin/availableWidget');
      const users = usersResponse.data?.users || [];
      const widgets = widgetsResponse.data?.availableWidgets || [];
      const uniqueUsers = new Map();
      const uniqueWidgets = new Map();
  
      users.forEach(user => {
        if (user?.id) {
          uniqueUsers.set(user.id, {
            _id: user.id,
            email: user.email,
            displayName: user.displayName,
          });
        }
      });
  
      widgets.forEach(widget => {
        if (widget?.id) {
          uniqueWidgets.set(widget.id, {
            _id: widget.id,
            name: widget.name,
          });
        }
      });
      // Convert Maps to arrays and update state
      setCustomers(Array.from(uniqueUsers.values()));
      setWidgetTypes(Array.from(uniqueWidgets.values()));
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  // Fetch subscriptions and payments data
  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const request_body = { collectionName: "orders" };

      const ordersResponse = await apiInstance.post(
        "/devadmin/collection",
        request_body
      );
      const data = ordersResponse.data.documents.widgetArray;
      setSubscriptions(data);
      setFilteredSubscriptions(data);
    } catch (err) {
      setError("Failed to load subscriptions");
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply all filters to subscription data
  const applyFilters = () => {
    let filtered = [...subscriptions];

    // Filter by name (case-insensitive)
    if (searchName) {
      filtered = filtered.filter((sub) =>
        sub.title?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    // Filter by customer
    if (selectedCustomer) {
      filtered = filtered.filter((sub) => sub.userId?._id === selectedCustomer);
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter((sub) => sub.subStatus === selectedStatus);
    }

    // Filter by widget type
    if (selectedType) {
      filtered = filtered.filter((sub) => sub.widgetType?._id === selectedType);
    }

    // Filter by price range
    if (minAmount && !isNaN(parseFloat(minAmount))) {
      filtered = filtered.filter(
        (sub) => (sub.widgetType?.price || 0) >= parseFloat(minAmount)
      );
    }

    if (maxAmount && !isNaN(parseFloat(maxAmount))) {
      filtered = filtered.filter(
        (sub) => (sub.widgetType?.price || 0) <= parseFloat(maxAmount)
      );
    }

    // Filter by date range
    if (dateFilterOption) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      let fromDate = new Date();

      if (dateFilterOption === "Today") {
        fromDate = today;
      } else if (dateFilterOption === "Last 7 days") {
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 7);
      } else if (dateFilterOption === "This month") {
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
      } else if (dateFilterOption === "Custom") {
        if (startDate) {
          fromDate = new Date(startDate);
          fromDate.setHours(0, 0, 0, 0);
        }

        filtered = filtered.filter((sub) => {
          if (!sub.createdAt) return false;

          const createdDate = new Date(sub.createdAt);

          // Apply start date filter if provided
          if (startDate && createdDate < fromDate) {
            return false;
          }

          // Apply end date filter if provided
          if (endDate) {
            const toDate = new Date(endDate);
            toDate.setHours(23, 59, 59, 999);
            if (createdDate > toDate) {
              return false;
            }
          }

          return true;
        });

        setFilteredSubscriptions(filtered);
        return;
      }

      filtered = filtered.filter((sub) => {
        if (!sub.createdAt) return false;

        const createdDate = new Date(sub.createdAt);
        return createdDate >= fromDate;
      });
    }

    setFilteredSubscriptions(filtered);
  };

  // Handle date filter option change
  const handleDateFilterChange = (option) => {
    setDateFilterOption(option);
    setShowCustomDateRange(option === "Custom");

    // Reset custom date range when changing filter
    if (option !== "Custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  // Format price display
  const formatPrice = (amount) => {
    return `$${amount?.toFixed(2) || "0.00"}/month`;
  };

  const cancelSubscription = async (widgetId) => {
    setSubscriptions((subscriptions) =>
        subscriptions.filter((sub) => sub._id !== widgetId)
    );
  };

  // Add new order to the list
  const addOrder = (newOrder) => {
    setNewOrder((newOrder) => !newOrder);
    setSubscriptions((prevSubscriptions) => [...prevSubscriptions, newOrder]);
  };

  return (
    <div className="mjwidev-main">
      <div className="mjwidev-main-top">
        {!selectedSubscription && (
          <>
            <h2>Our Orders</h2>
            <div
              className="mjwidev-add-avl-widget-btn mjwi-btn mjwidev-addnew"
              onClick={() => popupModalRef.current.openPopup("addOrder")}
            >
              Add Manual Order
            </div>
          </>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {!selectedSubscription && (
        <>
          <div className="mjwidev-filter-wrapper">
            {/* Search by name */}
            <div className="mjwidev-input-field mjwidev-width-40">
              <input
                type="text"
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                placeholder="Search by name"
              />
            </div>

            {/* Customer Dropdown */}
            <div className="mjwidev-input-field custom-select-wrapper mjwidev-width-40">
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <option value="">All Customers</option>
                {customers
                  .filter((customer) => customer?._id)
                  .map((customer) => (
                    <option
                      key={customer._id}
                      value={customer._id}
                      title={customer.email}
                    >
                      {`${customer.displayName?.trim()} (${customer.email?.trim()})` ||
                        "Unnamed Customer"}
                    </option>
                  ))}
              </select>
              <div   className="custom-arrow mjwidev-top-30">
                <svg
                  fill="#ffffff"
                  height="16px"
                  width="16px"
                  viewBox="0 0 330 330"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z" />
                </svg>
              </div>
            </div>

            {/* Status Dropdown */}
            <div className="mjwidev-input-field custom-select-wrapper mjwidev-width-40">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status.charAt(0).toUpperCase() +
                      status.slice(1).replace("_", " ")}
                  </option>
                ))}
              </select>
              <div   className="custom-arrow mjwidev-top-30">
                <svg
                  fill="#ffffff"
                  height="16px"
                  width="16px"
                  viewBox="0 0 330 330"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z" />
                </svg>
              </div>
            </div>

            {/* Type Dropdown */}
            <div className="mjwidev-input-field custom-select-wrapper mjwidev-width-40">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="">All Types</option>
                {widgetTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.name}
                  </option>
                ))}
              </select>
              <div   className="custom-arrow mjwidev-top-30">
                <svg
                  fill="#ffffff"
                  height="16px"
                  width="16px"
                  viewBox="0 0 330 330"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z" />
                </svg>
              </div>
            </div>

            {/* Date Filter */}
            <div className="mjwidev-input-field custom-select-wrapper mjwidev-width-40">
              <select
                value={dateFilterOption}
                onChange={(e) => handleDateFilterChange(e.target.value)}
              >
                <option value="">All Time</option>
                {dateFilterOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div   className="custom-arrow mjwidev-top-30">
                <svg
                  fill="#ffffff"
                  height="16px"
                  width="16px"
                  viewBox="0 0 330 330"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M325.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001l-139.39,139.393L25.607,79.393c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l150.004,150c2.813,2.813,6.628,4.393,10.606,4.393s7.794-1.581,10.606-4.394l149.996-150C331.465,94.749,331.465,85.251,325.607,79.393z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="mjwidev-filter-wrapper">
            {/* Amount Filters */}
            <div className="mjwidev-input-field ">
              <input
                type="number"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                placeholder="Min $"
              />
            </div>

            <div className="mjwidev-input-field">
              <input
                type="number"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                placeholder="Max $"
              />
            </div>
            {/* Custom Date Range */}
            {showCustomDateRange && (
              <>
                <div className="mjwidev-input-field mjwidev-width-40">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="mjwidev-input-field mjwidev-width-40">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}

      <SubscriptionsTable
        loading={loading}
        subscriptions={filteredSubscriptions}
        selectedSubscription={selectedSubscription}
        setSelectedSubscription={setSelectedSubscription}
        setSubscriptions={setSubscriptions}
        formatPrice={formatPrice}
        formatDate={formatDate}
        cancelSubscription={cancelSubscription}
      />

      {/* Popup components */}
      <PopupModal ref={popupModalRef} addOrder={addOrder} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.currentUser,
});

const mapDispatchToProps = {
  changeCurrentUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(DevadminOrders);

export const SubscriptionsTable = ({
  maxHeight = null,
  loading = false,
  subscriptions,
  selectedSubscription,
  setSelectedSubscription,
  setSubscriptions,
  formatPrice,
  formatDate,
  cancelSubscription,
}) => {
  const popupRef = useRef(null);
  const [initialData, setInitialData] = useState(null);

  const updateOrder = (updatedWidget) => {
    setSubscriptions((prevSubscriptions) => {
      return prevSubscriptions.map((sub) => {
        if (sub._id === updatedWidget.id || sub._id === updatedWidget._id) {
          return {
            ...sub,
            title: updatedWidget.title,
            expires: updatedWidget.expires,
            subStatus: updatedWidget.subStatus,
            payments: updatedWidget.payments,
          };
        }
        return sub;
      });
    });
  };

  const childUpdateOrder = (updatedWidget) => {
    const updatedSubscription = {
      ...initialData,
      title: updatedWidget.title,
      expires: updatedWidget.expires,
      subStatus: updatedWidget.subStatus,
    };
    updateOrder(updatedSubscription);
  };

  const handleEditPopup = (initialData) => {
    setInitialData(initialData);
    popupRef.current?.openPopup("editOrder");
  };

  return (
    <div
      style={maxHeight ? { height: maxHeight } : undefined}
      className="mjwi-setting-page mjwi-subscription-page"
    >
      {loading ? (
        <div>Loading subscriptions...</div>
      ) : selectedSubscription ? (
        <SubscriptionDetails
          selectedSubscription={selectedSubscription}
          parentUpdateOrder={updateOrder}
          setSelectedSubscription={setSelectedSubscription}
          onBack={() => setSelectedSubscription(null)}
        />
      ) : (
        <table className="mjwidev-table " border="1">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Status</th>
              <th>Name</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Created Date</th>
              <th>Renewal Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.length > 0 ? (
              subscriptions.map((subscription, index) => (
                <tr className="mjwi-subscription-table-row" key={index}>
                  <td
                    onClick={() => setSelectedSubscription(subscription)}
                  >
                    {subscription?.userId?.displayName || "N/A"}
                  </td>
                  <td
                    onClick={() => setSelectedSubscription(subscription)}
                  >
                    {subscription?.subStatus || "N/A"}
                  </td>
                  <td
                    onClick={() => setSelectedSubscription(subscription)}
                  >
                    {subscription.title || "N/A"}
                  </td>
                  <td
                    onClick={() => setSelectedSubscription(subscription)}
                  >
                    {subscription.widgetType?.name || "N/A"}
                  </td>
                  <td
                    onClick={() => setSelectedSubscription(subscription)}
                  >
                    {formatPrice(subscription.widgetType?.price)}
                  </td>
                  <td
                    onClick={() => setSelectedSubscription(subscription)}
                  >
                    {subscription.createdAt
                      ? formatDate(subscription.createdAt)
                      : "N/A"}
                  </td>
                  <td
                    onClick={() => setSelectedSubscription(subscription)}
                  >
                    {subscription.expires
                      ? formatDate(subscription.expires)
                      : "N/A"}
                  </td>
                  <td className="mjwidev-act-btns">
                    <i 
                        className="fa-solid fa-pen-to-square"
                        onClick={() => handleEditPopup(subscription)}
                    ></i>
                    <i 
                        className="fa-solid fa-trash"
                        onClick={(e) => {
                            e.stopPropagation();
                                popupRef.current.openPopup(
                                "deleteWidget",
                                { widgetId: subscription._id }
                            );
                        }}
                    ></i>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" style={{ textAlign: "center" }}>
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
      <PopupModal
        ref={popupRef}
        initialData={initialData}
        updateOrder={childUpdateOrder}
        deleteWidget={cancelSubscription}
      />
    </div>
  );
};
